import "@/App.css";
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  ArrowRight, MapPin, Users, Sparkles, ShieldCheck,
  TrendingUp, CheckCircle2, Phone, Mail, Building2, Home as HomeIcon, Ruler
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Section = ({ children, className = "" }) => (
  <section className={`max-w-[1280px] mx-auto px-5 sm:px-8 ${className}`}>{children}</section>
);

const Card = ({ children, className = "" }) => (
  <div className={`rounded-xl border border-[#3a2a14]/70 bg-gradient-to-b from-[#14100a] to-[#0d0905] ${className}`}>
    {children}
  </div>
);

const GoldButton = ({ children, className = "", ...props }) => (
  <button
    className={`bg-gradient-to-b from-[#e8c98a] to-[#c8a266] text-[#1a0f06] font-semibold tracking-wide px-6 py-3 rounded-md hover:from-[#f1d59c] hover:to-[#d4ac6f] transition-all shadow-[0_4px_20px_rgba(212,175,116,0.25)] disabled:opacity-50 ${className}`}
    {...props}
  >
    {children}
  </button>
);

const Logo = () => (
  <a href="/" className="flex items-center" data-testid="listproperty-logo">
    <div className="w-16 h-16 rounded-full overflow-hidden border border-[#d4a574]/40 flex items-center justify-center bg-[#0a0604]">
  <img src="/images/logo.png" alt="CineVibe logo" className="w-full h-full object-cover" />
</div>
  </a>
);

const scrollToEnquire = () => {
  const el = document.getElementById("enquire");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  else window.location.hash = "enquire";
};

const Header = ({ c }) => (
  <header className="sticky top-0 z-50 backdrop-blur-md bg-[#0a0604]/85 border-b border-[#d4a574]/15">
    <Section className="flex items-center justify-between py-4">
      <Logo />
      <div className="flex items-center gap-3">
        <a href="/" data-testid="back-home" className="text-[12px] tracking-[0.16em] text-[#d8cdb3] hover:text-[#f4d089]">{c?.header_back_label || "← BACK"}</a>
        <GoldButton data-testid="header-enquire" onClick={scrollToEnquire} className="text-sm tracking-[0.15em]">
          {c?.header_cta_label || "LIST NOW"}
        </GoldButton>
      </div>
    </Section>
  </header>
);

const Stat = ({ value, label }) => (
  <Card className="p-5 text-center">
    <div className="text-3xl font-display text-[#f4d089]">{value}</div>
    <div className="text-[11px] tracking-[0.18em] uppercase text-[#a89878] mt-2">{label}</div>
  </Card>
);

const ICONS = [TrendingUp, ShieldCheck, Users, Sparkles, HomeIcon, Ruler];
const STEP_ICONS = [<Phone size={18} />, <Building2 size={18} />, <Mail size={18} />, <Sparkles size={18} />];

const Feature = ({ icon: Icon, title, desc }) => (
  <Card className="p-6">
    <div className="w-10 h-10 rounded-md bg-[#1a0f06] border border-[#d4a574]/30 flex items-center justify-center text-[#f4d089]">
      <Icon size={18} />
    </div>
    <h4 className="text-[#f4d089] font-display text-lg mt-3">{title}</h4>
    <p className="text-sm text-[#bfb393] mt-2 leading-relaxed">{desc}</p>
  </Card>
);

const propertyTypes = ["Private Villa", "Rooftop", "Banquet Hall", "Home Theatre", "Farmhouse", "Other"];
const capacities = ["2-10 guests", "10-25 guests", "25-50 guests", "50-100 guests", "100+ guests"];
const availabilities = ["Weekends", "Weekdays", "Both", "Flexible"];

function EnquireForm({ c }) {
  const [f, setF] = useState({
    name: "", phone: "", email: "", city: "",
    property_type: "", location: "", capacity: "", area_sqft: "", availability: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr("");
    if (!f.name.trim()) return setErr("Please enter your name");
    if (f.phone.replace(/\D/g, "").length < 10) return setErr("Please enter a valid 10-digit phone");
    setBusy(true);
    try {
      await axios.post(`${API}/property/leads`, f);
      setDone(true);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Card className="p-10 text-center" data-testid="listprop-success">
        <CheckCircle2 className="mx-auto text-[#7ad99c]" size={48} />
        <h3 className="font-display text-2xl text-[#f4d089] mt-4">{c?.form_success_title || "Thank you,"} {f.name.split(" ")[0]}!</h3>
        <p className="text-[#bfb393] mt-2 max-w-md mx-auto">{c?.form_success_msg || "Your property enquiry is in. Our onboarding team will reach out within 24 hours."}</p>
        <button
          data-testid="listprop-another"
          onClick={() => { setDone(false); setF({ name: "", phone: "", email: "", city: "", property_type: "", location: "", capacity: "", area_sqft: "", availability: "", message: "" }); }}
          className="mt-6 text-xs tracking-widest text-[#f4d089] underline"
        >
          SUBMIT ANOTHER ENQUIRY
        </button>
      </Card>
    );
  }

  const inputCls = "w-full bg-[#0a0604] border border-[#3a2a14] rounded-md px-3 py-2.5 text-sm text-white placeholder:text-[#7a6c54] focus:outline-none focus:border-[#d4a574]";
  const labelCls = "text-[11px] tracking-[0.18em] uppercase text-[#a89878] mb-1.5 block";

  return (
    <Card className="p-6 sm:p-8">
      <div className="text-[11px] tracking-[0.2em] text-[#d4a574]">{c?.form_card_eyebrow || "PROPERTY ENQUIRY"}</div>
      <h3 className="font-display text-2xl sm:text-3xl text-white mt-1">{c?.form_card_heading || "Tell us about your property"}</h3>
      <p className="text-[#bfb393] text-sm mt-1">{c?.form_card_subtitle || "We'll get back within 24 hours with onboarding details."}</p>

      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        <div><label className={labelCls}>Full Name *</label><input data-testid="lp-name" className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your full name" /></div>
        <div><label className={labelCls}>Phone *</label><input data-testid="lp-phone" className={inputCls} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="10-digit mobile" /></div>
        <div><label className={labelCls}>Email</label><input data-testid="lp-email" className={inputCls} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="you@example.com" /></div>
        <div><label className={labelCls}>City</label><input data-testid="lp-city" className={inputCls} value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} placeholder="e.g. Pune" /></div>
        <div><label className={labelCls}>Property Type</label>
          <select data-testid="lp-type" className={inputCls} value={f.property_type} onChange={(e) => setF({ ...f, property_type: e.target.value })}>
            <option value="">Select type</option>
            {propertyTypes.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div><label className={labelCls}>Capacity</label>
          <select data-testid="lp-capacity" className={inputCls} value={f.capacity} onChange={(e) => setF({ ...f, capacity: e.target.value })}>
            <option value="">Select capacity</option>
            {capacities.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div><label className={labelCls}>Location / Area</label><input data-testid="lp-location" className={inputCls} value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="e.g. Koregaon Park" /></div>
        <div><label className={labelCls}>Carpet Area (sqft)</label><input data-testid="lp-area" className={inputCls} value={f.area_sqft} onChange={(e) => setF({ ...f, area_sqft: e.target.value })} placeholder="e.g. 1800" /></div>
        <div className="sm:col-span-2"><label className={labelCls}>Availability</label>
          <div className="flex flex-wrap gap-2">
            {availabilities.map((t) => (
              <button key={t} type="button"
                data-testid={`lp-availability-${t.toLowerCase().replace(/\s|-/g, "_")}`}
                onClick={() => setF({ ...f, availability: t })}
                className={`text-xs px-3 py-2 rounded-md border transition ${f.availability === t ? "border-[#d4a574] bg-[#1a0f06] text-[#f4d089]" : "border-[#3a2a14] text-[#bfb393] hover:border-[#d4a574]/60"}`}>
                {t}
              </button>
            ))}
          </div>
        </div>
        <div className="sm:col-span-2"><label className={labelCls}>Anything about your property?</label><textarea data-testid="lp-message" rows={3} className={inputCls} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Amenities, unique features, photos link…" /></div>
      </div>

      {err && <div data-testid="lp-error" className="text-red-400 text-sm mt-4">{err}</div>}

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
        <GoldButton data-testid="lp-submit" onClick={submit} disabled={busy} className="text-sm tracking-[0.15em] inline-flex items-center gap-2 justify-center">
          {busy ? "Submitting…" : (c?.form_submit_label || "SUBMIT ENQUIRY")} <ArrowRight size={16} />
        </GoldButton>
        <span className="text-[11px] text-[#a89878]">{c?.form_disclaimer || "By submitting, you agree to be contacted by The CineVibe onboarding team."}</span>
      </div>
    </Card>
  );
}

export default function ListProperty() {
  const [c, setC] = useState({});

  useEffect(() => {
    axios.get(`${API}/content/list_property`).then((r) => setC(r.data || {})).catch(() => setC({}));
  }, []);

  const stats = [
    { v: c?.stat1_value || "0%",      l: c?.stat1_label || "Listing Fee" },
    { v: c?.stat2_value || "Premium", l: c?.stat2_label || "Guest Profile" },
    { v: c?.stat3_value || "T+2",     l: c?.stat3_label || "Payouts" },
    { v: c?.stat4_value || "1,200+",  l: c?.stat4_label || "Happy Guests" },
  ];

  const features = (c?.features && c.features.length ? c.features : [
    { title: "Premium Bookings", desc: "Get bookings from high-intent customers for celebrations, proposals, screenings & corporate events." },
    { title: "Zero Hassle",      desc: "We handle bookings, payments, support and on-site décor. You just host the space." },
    { title: "Verified Guests",  desc: "OTP-verified customers, damage protection & clear T&Cs for every booking." },
    { title: "Pro Styling",      desc: "Our crew adds lights, screens and décor that make your property Instagram-worthy." },
    { title: "Flexible Slots",   desc: "List only the time slots you want. Weekends, evenings, full-day — you choose." },
    { title: "Any Size",         desc: "From cosy 2-guest rooms to 100+ guest rooftops — if it's well-kept, it fits." },
  ]).slice(0, 6);

  const steps = (c?.steps && c.steps.length ? c.steps : [
    { title: "1. Enquire", desc: "Submit the form. Our onboarding team calls within 24 hours." },
    { title: "2. Verify",  desc: "Site visit, photos, quick checks for safety & quality." },
    { title: "3. Go Live", desc: "Your property goes live on The CineVibe with pro photos & copy." },
    { title: "4. Earn",    desc: "Receive bookings, host guests, get paid on T+2 cycles." },
  ]).slice(0, 4);

  return (
    <div className="bg-[#0a0604] min-h-screen text-white" data-testid="list-property-page">
      <Header c={c} />
      {/* Hero */}
      <Section className="pt-12 pb-10">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] text-[#d4a574] border border-[#d4a574]/30 rounded-full px-3 py-1">
              <Sparkles size={12} /> {c?.hero_eyebrow || "LIST YOUR PROPERTY"}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-5">
              {c?.hero_title_pre || "Earn with "}<span className="text-[#f4d089]">{c?.hero_title_highlight || "The CineVibe"}</span>{c?.hero_title_post || ". Your space. Our guests."}
            </h1>
            <p className="text-[#bfb393] mt-5 max-w-xl leading-relaxed">{c?.hero_subtitle || "List your villa, rooftop, banquet or theatre on The CineVibe and get bookings from premium customers — celebrations, screenings, corporate events & more."}</p>
            <div className="flex flex-wrap gap-3 mt-7">
              <GoldButton data-testid="hero-list" onClick={scrollToEnquire} className="text-sm tracking-[0.15em]">
                {c?.hero_cta_label || "LIST YOUR PROPERTY"}
              </GoldButton>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {stats.map((s, i) => <Stat key={i} value={s.v} label={s.l} />)}
          </div>
        </div>
      </Section>

      {/* Why */}
      <Section className="py-14" id="why">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-[11px] tracking-[0.2em] text-[#d4a574]">{c?.why_eyebrow || "WHY LIST WITH US"}</div>
          <h2 className="font-display text-3xl sm:text-4xl mt-2">{c?.why_heading || "Turn your space into a revenue engine"}</h2>
          <p className="text-[#bfb393] text-sm mt-3">{c?.why_subtitle || "We bring the guests, the setup crew and the premium branding."}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {features.map((f, i) => <Feature key={i} icon={ICONS[i] || Sparkles} title={f.title} desc={f.desc} />)}
        </div>
      </Section>

      {/* How it works */}
      <Section className="py-14">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-[11px] tracking-[0.2em] text-[#d4a574]">{c?.how_eyebrow || "HOW IT WORKS"}</div>
          <h2 className="font-display text-3xl sm:text-4xl mt-2">{c?.how_heading || "From enquiry to first payout"}</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
          {steps.map((s, i) => (
            <Card key={i} className="p-5">
              <div className="w-10 h-10 rounded-md bg-[#1a0f06] border border-[#d4a574]/30 flex items-center justify-center text-[#f4d089]">{STEP_ICONS[i] || <Sparkles size={18} />}</div>
              <div className="font-display text-lg text-[#f4d089] mt-3">{s.title}</div>
              <div className="text-sm text-[#bfb393] mt-2 leading-relaxed">{s.desc}</div>
            </Card>
          ))}
        </div>
      </Section>

      {/* Form */}
      <Section className="py-14">
        <div id="enquire" className="scroll-mt-24" />
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2">
            <div className="text-[11px] tracking-[0.2em] text-[#d4a574]">{c?.form_eyebrow || "GET IN TOUCH"}</div>
            <h2 className="font-display text-3xl sm:text-4xl mt-2">{c?.form_heading || "List your property in minutes."}</h2>
            <p className="text-[#bfb393] mt-4 leading-relaxed">{c?.form_subtitle || "Drop your details and our onboarding team will share the complete partner pack with revenue share, terms and next steps."}</p>
            <div className="mt-6 space-y-3">
              <div className="flex items-center gap-3 text-sm text-[#d8cdb3]"><Phone size={16} className="text-[#f4d089]" /> {c?.form_phone || "+91 90000 00000"}</div>
              <div className="flex items-center gap-3 text-sm text-[#d8cdb3]"><Mail size={16} className="text-[#f4d089]" /> {c?.form_email || "partners@cinevibe.in"}</div>
              <div className="flex items-center gap-3 text-sm text-[#d8cdb3]"><MapPin size={16} className="text-[#f4d089]" /> {c?.form_location || "Pan-India onboarding"}</div>
            </div>
          </div>
          <div className="lg:col-span-3">
            <EnquireForm c={c} />
          </div>
        </div>
      </Section>

      <footer className="border-t border-[#d4a574]/15 mt-10">
        <Section className="py-8 flex flex-col sm:flex-row justify-between gap-4 text-xs text-[#a89878]">
          <div className="leading-relaxed">
            © {new Date().getFullYear()} {c?.footer_text || "The CineVibe — Private Screens & Café"}<br />
            BnBBuddy Property Management Services Pvt Ltd · GSTIN: 07AALCB5876H1Z8
          </div>
          <a href="/" className="hover:text-[#f4d089]">Back to Home</a>
        </Section>
      </footer>
    </div>
  );
}
